import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast, { Toaster } from 'react-hot-toast'
import { Link, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'
import MainButton from '../../components/buttons/mainButton/MainButton'
import Field from '../../components/textField/Field'
import { translations } from '../../configs/translations'
import type { Language } from '../../context/LanguageContext'
import AuthService from '../../services/AuthService'
import { ClientApi } from '../../services/clientApi'
import UserService from '../../services/UserService'
import { useStore } from '../../store/store'
import { NuiCallbacks } from '../../utils/ClientEvents'
import { decodeJwtExp } from '../../utils/decodeJwtExp'
import './Login.css'

const schema = yup.object({
    email: yup
        .string()
        .required('Email is required'),
    password: yup
        .string()
        .required('Password is required')
})

type FormData = yup.InferType<typeof schema>

const Login = () => {
    const [language, setLanguage] = useState<Language>('ru')
    const [searchParams] = useSearchParams()
    const t = translations[language]
    const login = useStore(state => state.login)
    const storedPlayerId = localStorage.getItem('gameId')
    const playerId = Number(searchParams.get('playerId') || storedPlayerId || -1)
    const isSent = useRef(false)
    const [checkingAuth, setCheckingAuth] = useState(true)

    const {
        register: registerField,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm<FormData>({
        resolver: yupResolver(schema),
        mode: 'onChange'
    })

    const openLobby = async () => {
        await UserService.openLobby()
            .catch((error) => {
                console.error(error.response)
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : 'error unknown',
                    { className: 'notification-error' }
                )
            })
    }

    const onSubmit = async (data: FormData) => {
        await login(data.email, data.password, playerId)
            .then(openLobby)
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const email = watch('email')
    const password = watch('password')

    useEffect(() => {
        const tryLoginWithAccessToken = async () => {
            const data = await AuthService.loginWithAccessToken(playerId)
            useStore.setState({ userId: data.userId })
            localStorage.setItem('userId', String(data.userId))
            useStore.setState({ userName: data.userName })
        }

        const tryRefreshAccessToken = async () => {
            return await AuthService.refreshAccessToken()
        }

        const hidePreloader = () => {
            ClientApi.send(NuiCallbacks.HIDE_PRELOADER, {})
            setCheckingAuth(false)
        }

        const tryFetchJwtFromClient = async () => {
            if (isSent.current) return
            isSent.current = true
            try {
                await AuthService.tryFetchJwtFromClient().then((data) => {
                    if (data) {
                        tryLoginWithAccessToken()
                            .then(openLobby)
                            .catch((error) => {
                                const refreshToken = localStorage.getItem('refreshToken')
                                const exp = decodeJwtExp(refreshToken)
                                if (!exp) {
                                    isSent.current = false
                                    hidePreloader()
                                    return
                                }
                                const expiration = new Date(exp * 1000)

                                const nowMs = Date.now()
                                const fiveDaysMs = 5 * 24 * 60 * 60 * 1000

                                if (expiration.getTime() - nowMs >= fiveDaysMs) {
                                    tryRefreshAccessToken()
                                        .then(() => {
                                            tryLoginWithAccessToken()
                                                .then(openLobby)
                                                .catch((error) => {
                                                    console.error(error)
                                                    isSent.current = false
                                                    hidePreloader()
                                                })
                                        })
                                        .catch((error) => {
                                            console.error(error)
                                            isSent.current = false
                                            hidePreloader()
                                        })
                                } else {
                                    hidePreloader()
                                }
                            })
                            .catch((error) => {
                                console.error(error)
                                isSent.current = false
                                hidePreloader()
                            })
                    } else {
                        hidePreloader()
                    }
                })
            } catch (error) {
                console.error(error)
                isSent.current = false
                hidePreloader()
            }
        }
        tryFetchJwtFromClient()
    }, [])

    useEffect(() => {
        if (playerId !== -1) {
            localStorage.setItem('gameId', String(playerId))
        }
    }, [playerId])

    if (checkingAuth) return null

    return (
        <>
            <div className="container">
                <div className="window">
                    <h1 className='title'>{t.welcome}</h1>
                    <form onSubmit={(e) => {
                        e.preventDefault()
                        handleSubmit(onSubmit)(e)
                    }} className='fields'>
                        <Field
                            value={email}
                            label={t.username_email}
                            placeholder={t.enter_username_email}
                            error={errors.email?.message}
                            type="text"
                            {...registerField('email')}
                        />
                        <Field
                            value={password}
                            label={t.password}
                            placeholder={t.enter_password}
                            error={errors.password?.message}
                            type="password"
                            {...registerField('password')}
                        />

                        <MainButton text={t.login} type="submit" />
                    </form>

                    <div className="links">
                        <Link to="/passwordRecovery">{t.forgot_password}</Link>
                        <p>{t.dont_have_account} <Link to="/register">{t.signup}</Link></p>
                        <Link to="/terms">{t.terms_of_service}</Link>
                    </div>

                    <Toaster
                        position="bottom-center"
                        toastOptions={{
                            duration: 2000,
                        }}
                    />
                </div>
            </div>
        </>
    )
}

export default Login 